import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import {
  Box, Typography, Grid, MenuItem, TextField, Button, CircularProgress,
  Alert, Snackbar, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Divider, IconButton, Tooltip, Collapse,
  Dialog, DialogTitle, DialogContent, DialogActions, List, ListItem,
  ListItemText, ListItemSecondaryAction, Badge, Chip,
} from '@mui/material';
import {
  Add, Delete, Warning, CheckCircle, Cancel,
  WifiOff, Sync, Storage, Close, AccessTime, Person, Groups,
} from '@mui/icons-material';
import axios from 'axios';
import { BASE_URL, apiCall } from '../../api/apiClient';

// ── Shared design tokens ───────────────────────────────────────────────────
const BG        = '#f5f8f6';
const SURFACE   = '#ffffff';
const BORDER    = '#d0ddd7';
const ACCENT    = '#2e7d5e';
const ACCENT_BG = '#e8f5ee';
const TEXT_PRI  = '#0f1f14';
const TEXT_SEC  = '#4b6358';
const WARN_BG   = '#fff8e1';
const WARN_BOR  = '#ffe082';
// ──────────────────────────────────────────────────────────────────────────

const menuProps = {
  PaperProps: {
    sx: {
      bgcolor: SURFACE, border: `1px solid ${BORDER}`, borderRadius: '10px',
      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
      '& .MuiMenuItem-root': {
        color: TEXT_PRI, fontSize: 14,
        '&:hover': { bgcolor: ACCENT_BG },
        '&.Mui-selected': { bgcolor: ACCENT_BG, color: ACCENT, fontWeight: 600 },
        '&.Mui-selected:hover': { bgcolor: ACCENT_BG },
      },
    },
  },
};

const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px', bgcolor: SURFACE, color: TEXT_PRI,
    '& fieldset': { borderColor: BORDER },
    '&:hover fieldset': { borderColor: ACCENT },
    '&.Mui-focused fieldset': { borderColor: ACCENT },
  },
  '& .MuiInputLabel-root': { color: TEXT_SEC, fontSize: 13 },
  '& .MuiInputLabel-root.Mui-focused': { color: ACCENT },
  '& .MuiSelect-icon': { color: TEXT_SEC },
};

// ── IndexedDB helpers ─────────────────────────────────────────────────────
const DB_NAME    = 'pwa_faults_db';
const DB_VERSION = 1;
const STORE_NAME = 'pending_faults';

function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME))
        db.createObjectStore(STORE_NAME, { keyPath: 'id', autoIncrement: true });
    };
    req.onsuccess = (e) => resolve(e.target.result);
    req.onerror   = (e) => reject(e.target.error);
  });
}

async function savePendingFault(gameId, fault) {
  const db = await openDB();
  const tx = db.transaction(STORE_NAME, 'readwrite');
  return new Promise((resolve, reject) => {
    const req = tx.objectStore(STORE_NAME).add({ gameId, fault, savedAt: Date.now() });
    req.onsuccess = () => resolve(req.result);
    req.onerror   = (e) => reject(e.target.error);
  });
}

async function getPendingFaultsByGame(gameId) {
  const db = await openDB();
  const tx = db.transaction(STORE_NAME, 'readonly');
  return new Promise((resolve, reject) => {
    const req = tx.objectStore(STORE_NAME).getAll();
    req.onsuccess = () => resolve((req.result || []).filter((r) => r.gameId === gameId));
    req.onerror   = (e) => reject(e.target.error);
  });
}

async function deletePendingFaultById(id) {
  const db = await openDB();
  const tx = db.transaction(STORE_NAME, 'readwrite');
  return new Promise((resolve, reject) => {
    const req = tx.objectStore(STORE_NAME).delete(id);
    req.onsuccess = () => resolve();
    req.onerror   = (e) => reject(e.target.error);
  });
}

async function deletePendingFaultsByGame(gameId) {
  const db     = await openDB();
  const tx     = db.transaction(STORE_NAME, 'readwrite');
  const store  = tx.objectStore(STORE_NAME);
  const allReq = store.getAll();
  return new Promise((resolve, reject) => {
    allReq.onsuccess = () => {
      const toDelete = (allReq.result || []).filter((r) => r.gameId === gameId);
      let remaining  = toDelete.length;
      if (remaining === 0) { resolve(); return; }
      toDelete.forEach((item) => {
        const del = store.delete(item.id);
        del.onsuccess = () => { if (--remaining === 0) resolve(); };
        del.onerror   = (e) => reject(e.target.error);
      });
    };
    allReq.onerror = (e) => reject(e.target.error);
  });
}

const isOnline = () => navigator.onLine;

// ── Helpers ───────────────────────────────────────────────────────────────
const getUniquePlayers = (players = []) => {
  const map = new Map();
  players.forEach(({ entityId, userIds }) => {
    (userIds || []).forEach((u) => {
      if (!map.has(u._id)) map.set(u._id, { ...u, entityId });
    });
  });
  return [...map.values()];
};

const playerLabel = (p) =>
  `${p.firstName} ${p.lastName}${p.actualJerseyNumber ? ` #${p.actualJerseyNumber}` : ''}`;

const fmtTime = (ts) =>
  new Date(ts).toLocaleString('fr-FR', {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
  });

const CARD_TYPES = [
  { value: 'N/A',    label: '⚠️ Faute' },
  { value: 'Yellow', label: '🟨 Carton jaune' },
  { value: 'Red',    label: '🟥 Carton rouge' },
];

const getCardLabel = (cardType) =>
  CARD_TYPES.find((c) => c.value === cardType)?.label || cardType || '—';

// ── OfflineFaultsDialog ───────────────────────────────────────────────────
const OfflineFaultsDialog = ({
  open, onClose, pendingRows, allPlayers, entities,
  onDelete, onDeleteAll, syncing, onSync, online,
}) => {
  const playerMap = useMemo(() => {
    const m = new Map(); allPlayers.forEach((p) => m.set(p._id, p)); return m;
  }, [allPlayers]);

  const entityMap = useMemo(() => {
    const m = new Map(); entities.forEach((e) => m.set(e._id, e)); return m;
  }, [entities]);

  const resolvePlayer = (id) => {
    const p = playerMap.get(id);
    return p ? playerLabel(p) : id || '—';
  };
  const resolveEntity = (id) => entityMap.get(id)?.name || id || '—';

  return (
    <Dialog
      open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px', border: `1px solid ${WARN_BOR}`,
          bgcolor: SURFACE, boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
        },
      }}
    >
      {/* Header */}
      <DialogTitle sx={{
        bgcolor: WARN_BG, borderBottom: `1px solid ${WARN_BOR}`,
        py: 1.5, px: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Storage fontSize="small" sx={{ color: '#b45309' }} />
          <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#92400e' }}>
            Fautes hors-ligne ({pendingRows.length})
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ color: TEXT_SEC }}>
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>

      {/* Content */}
      <DialogContent sx={{ p: 0 }}>
        {pendingRows.length === 0 ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: TEXT_SEC }}>Aucune faute en attente.</Typography>
          </Box>
        ) : (
          <List disablePadding>
            {pendingRows.map((row, idx) => {
              const { fault, savedAt, id } = row;
              return (
                <React.Fragment key={id ?? idx}>
                  <ListItem
                    alignItems="flex-start"
                    sx={{
                      px: 2, py: 1.25,
                      borderBottom: idx < pendingRows.length - 1 ? `1px solid ${BORDER}` : 'none',
                      '&:hover': { bgcolor: BG },
                      transition: 'background 0.15s',
                    }}
                  >
                    {/* Index badge */}
                    <Box sx={{
                      minWidth: 32, height: 32, borderRadius: '50%',
                      bgcolor: WARN_BG, border: `1px solid ${WARN_BOR}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      mr: 1.5, mt: 0.25, flexShrink: 0,
                    }}>
                      <Typography variant="caption" fontWeight={700} sx={{ color: '#b45309' }}>
                        {idx + 1}
                      </Typography>
                    </Box>

                    <ListItemText
                      disableTypography
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75, mb: 0.5 }}>
                          {/* Minute */}
                          <Chip
                            icon={<AccessTime sx={{ fontSize: '0.75rem !important' }} />}
                            label={`${fault.minute}'`}
                            size="small"
                            sx={{ bgcolor: ACCENT_BG, color: ACCENT, fontWeight: 700, border: `1px solid ${BORDER}`, fontSize: '0.72rem' }}
                          />
                          {/* Card type */}
                          <Chip
                            label={getCardLabel(fault.CardType)}
                            size="small"
                            variant="outlined"
                            sx={{ borderColor: BORDER, color: TEXT_SEC, fontSize: '0.7rem' }}
                          />
                        </Box>
                      }
                      secondary={
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                          {/* Player */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Warning sx={{ fontSize: '0.85rem', color: '#d97706' }} />
                            <Typography variant="caption" sx={{ color: TEXT_PRI, fontWeight: 600 }}>
                              {resolvePlayer(fault.userId)}
                            </Typography>
                          </Box>
                          {/* Team */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Groups sx={{ fontSize: '0.85rem', color: TEXT_SEC }} />
                            <Typography variant="caption" sx={{ color: TEXT_SEC }}>
                              {resolveEntity(fault.entityId)}
                            </Typography>
                          </Box>
                          {/* Saved at */}
                          <Typography variant="caption" sx={{ color: '#b45309', fontSize: '0.67rem', mt: 0.2 }}>
                            Sauvegardé le {fmtTime(savedAt)}
                          </Typography>
                        </Box>
                      }
                    />

                    <ListItemSecondaryAction>
                      <Tooltip title="Supprimer cette faute hors-ligne">
                        <IconButton
                          edge="end" size="small" onClick={() => onDelete(id)}
                          sx={{
                            color: '#c62828', bgcolor: '#fdecea', borderRadius: '6px',
                            '&:hover': { bgcolor: '#f9cbc8' },
                          }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </ListItemSecondaryAction>
                  </ListItem>
                </React.Fragment>
              );
            })}
          </List>
        )}
      </DialogContent>

      {/* Footer */}
      {pendingRows.length > 0 && (
        <DialogActions sx={{ px: 2, py: 1.5, borderTop: `1px solid ${BORDER}`, justifyContent: 'space-between' }}>
          <Button
            size="small" variant="outlined" color="error"
            startIcon={<Delete fontSize="small" />}
            onClick={onDeleteAll}
            sx={{ textTransform: 'none', borderRadius: '8px', fontWeight: 600 }}
          >
            Tout supprimer
          </Button>
          {online && (
            <Button
              size="small" variant="contained" disabled={syncing}
              onClick={onSync}
              startIcon={syncing ? <CircularProgress size={13} color="inherit" /> : <Sync fontSize="small" />}
              sx={{
                bgcolor: ACCENT, borderRadius: '8px', textTransform: 'none',
                fontWeight: 600, color: '#fff', '&:hover': { bgcolor: '#235f47' },
              }}
            >
              {syncing ? 'Synchronisation…' : 'Synchroniser maintenant'}
            </Button>
          )}
        </DialogActions>
      )}
    </Dialog>
  );
};

// ── Main component ────────────────────────────────────────────────────────
const FaultsTab = ({ game, onRefresh }) => {
  const [form, setForm]               = useState({ entityId: '', userId: '', CardType: '', minute: '' });
  const [loading, setLoading]         = useState(false);
  const [syncing, setSyncing]         = useState(false);
  const [deletingId, setDeletingId]   = useState(null);
  const [confirmId, setConfirmId]     = useState(null);
  const [error, setError]             = useState('');
  const [snack, setSnack]             = useState({ open: false, message: '', severity: 'success' });
  const [pendingCount, setPendingCount] = useState(0);
  const [pendingRows, setPendingRows]   = useState([]);
  const [dialogOpen, setDialogOpen]     = useState(false);
  const [online, setOnline]           = useState(isOnline());
  const [canAddFault, setCanAddFault] = useState(false);

  const syncLockRef = useRef(false);

  // ── Refresh pending list ──
  const refreshPending = useCallback(async () => {
    try {
      const rows = await getPendingFaultsByGame(game._id);
      setPendingRows(rows);
      setPendingCount(rows.length);
    } catch {}
  }, [game._id]);

  // ── Sync to server ──
  const syncPendingFaults = useCallback(async () => {
    if (syncLockRef.current || !isOnline()) return;
    syncLockRef.current = true;
    setSyncing(true);
    try {
      const pending = await getPendingFaultsByGame(game._id);
      if (pending.length === 0) { setSyncing(false); syncLockRef.current = false; return; }

      await apiCall((headers) =>
        axios.post(
          `${BASE_URL}/games/${game._id}/faults/bulk`,
          { faults: pending.map((r) => r.fault) },
          { headers }
        )
      );

      await deletePendingFaultsByGame(game._id);
      setPendingRows([]);
      setPendingCount(0);
      setDialogOpen(false);
      setSnack({ open: true, message: `${pending.length} faute(s) synchronisée(s) avec succès !`, severity: 'success' });
      onRefresh();
    } catch {
      setSnack({ open: true, message: 'Échec de la synchronisation, réessai ultérieur…', severity: 'warning' });
    } finally {
      setSyncing(false);
      syncLockRef.current = false;
    }
  }, [game._id, onRefresh]);

  // ── Delete single pending ──
  const handleDeletePending = useCallback(async (id) => {
    try { await deletePendingFaultById(id); await refreshPending(); } catch {}
  }, [refreshPending]);

  // ── Delete all pending ──
  const handleDeleteAllPending = useCallback(async () => {
    try {
      await deletePendingFaultsByGame(game._id);
      setPendingRows([]); setPendingCount(0); setDialogOpen(false);
    } catch {}
  }, [game._id]);

  // ── Open dialog ──
  const handleOpenDialog = useCallback(async () => {
    await refreshPending(); setDialogOpen(true);
  }, [refreshPending]);

  // ── Online/offline listeners ──
  useEffect(() => {
    const handleOnline  = () => { setOnline(true);  syncPendingFaults(); };
    const handleOffline = () => setOnline(false);
    window.addEventListener('online',  handleOnline);
    window.addEventListener('offline', handleOffline);
    refreshPending();
    if (isOnline()) syncPendingFaults();
    return () => {
      window.removeEventListener('online',  handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [syncPendingFaults, refreshPending]);

  // ── Permission check ──
  // useEffect(() => {
  //   const currentUser = (() => {
  //     try { const u = localStorage.getItem('user'); return u ? JSON.parse(u) : null; } catch { return null; }
  //   })();
  //   if (!currentUser) return;
  //   if (currentUser.role === 'entityPlatformAdmin') { setCanAddFault(true); return; }
  //   const ownerEntry = (game?.eventOwnersAndRoles || []).find((e) => e.userId?._id === currentUser._id);
  //   if (!ownerEntry) return;
  //   const roles = ownerEntry.roles || [];
  //   setCanAddFault(roles.includes('eventSuperAdmin') || roles.includes('eventGameUpdater'));
  // }, [game?._id]);

  // ── Permission check ──
useEffect(() => {
  const currentUser = (() => {
    try { const u = localStorage.getItem('user'); return u ? JSON.parse(u) : null; } catch { return null; }
  })();
  if (!currentUser) return;
  const ownerEntry = (game?.eventOwnersAndRoles || []).find((e) => e.userId?._id === currentUser._id);
  if (!ownerEntry) return;
  setCanAddFault(true);
}, [game?._id]);

  // ── Derived data ──
  const entities = useMemo(() => {
    const map = new Map();
    (game?.players || []).forEach(({ entityId }) => { if (entityId?._id) map.set(entityId._id, entityId); });
    return [...map.values()];
  }, [game]);

  const allPlayers = useMemo(() => getUniquePlayers(game?.players || []), [game]);

  const playersForEntity = useMemo(() => {
    if (!form.entityId) return [];
    return allPlayers.filter((p) => p.entityId?._id === form.entityId);
  }, [form.entityId, allPlayers]);

  const handleChange       = (field) => (e) => setForm((p) => ({ ...p, [field]: e.target.value }));
  const handleEntityChange = (e) => setForm((p) => ({ ...p, entityId: e.target.value, userId: '' }));

  // ── Submit ──
  const handleSubmit = async () => {
    if (!form.entityId || !form.userId || !form.CardType || !form.minute) {
      setError('Tous les champs sont obligatoires'); return;
    }
    setLoading(true); setError('');

    const faultPayload = {
      entityId: form.entityId,
      userId:   form.userId,
      CardType: form.CardType,
      minute:   Number(form.minute),
    };

    if (isOnline()) {
      try {
        await apiCall((headers) =>
          axios.post(`${BASE_URL}/games/${game._id}/faults`, faultPayload, { headers })
        );
        setSnack({ open: true, message: 'Faute ajoutée avec succès !', severity: 'success' });
        setForm({ entityId: '', userId: '', CardType: '', minute: '' });
        onRefresh();
      } catch (err) {
        setError(err.response?.data?.message || "Échec de l'ajout de la faute");
      }
    } else {
      try {
        await savePendingFault(game._id, faultPayload);
        await refreshPending();
        setSnack({
          open: true,
          message: 'Hors-ligne — faute sauvegardée localement, sera synchronisée à la reconnexion.',
          severity: 'warning',
        });
        setForm({ entityId: '', userId: '', CardType: '', minute: '' });
      } catch {
        setError('Impossible de sauvegarder la faute localement');
      }
    }
    setLoading(false);
  };

  const handleDeleteRequest = (faultId) => { setConfirmId(faultId); setError(''); };
  const handleDeleteCancel  = () => setConfirmId(null);

  const handleDeleteConfirm = async () => {
    const faultId = confirmId;
    setConfirmId(null); setDeletingId(faultId); setError('');
    try {
      await apiCall((headers) =>
        axios.delete(`${BASE_URL}/games/${game._id}/faults/${faultId}`, { headers })
      );
      setDeletingId(null);
      setSnack({ open: true, message: 'Faute supprimée avec succès !', severity: 'success' });
      onRefresh();
    } catch (err) {
      setDeletingId(null);
      setError(err.response?.data?.message || 'Échec de la suppression de la faute');
    }
  };

  const faults = game?.Faults || [];

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ color: TEXT_PRI }}>

      {/* ── Offline / pending banner ── */}
      {(!online || pendingCount > 0) && (
        <Alert
          severity={online ? 'info' : 'warning'}
          icon={online ? <Sync fontSize="small" /> : <WifiOff fontSize="small" />}
          sx={{ mb: 1.5, borderRadius: '10px' }}
          action={
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {pendingCount > 0 && (
                <Badge badgeContent={pendingCount} color="warning" overlap="rectangular">
                  <Button
                    size="small" variant="outlined"
                    onClick={handleOpenDialog}
                    startIcon={<Storage fontSize="small" />}
                    sx={{
                      textTransform: 'none', fontWeight: 600,
                      borderColor: WARN_BOR, color: '#92400e', borderRadius: '7px',
                      '&:hover': { bgcolor: WARN_BG, borderColor: '#f59e0b' },
                    }}
                  >
                    Voir
                  </Button>
                </Badge>
              )}
              {online && pendingCount > 0 && (
                <Button
                  size="small" onClick={syncPendingFaults} disabled={syncing}
                  startIcon={syncing ? <CircularProgress size={12} color="inherit" /> : <Sync fontSize="small" />}
                  sx={{ textTransform: 'none', color: ACCENT, fontWeight: 600 }}
                >
                  Synchroniser
                </Button>
              )}
            </Box>
          }
        >
          {!online && pendingCount === 0 && 'Hors-ligne — les fautes seront sauvegardées localement.'}
          {!online && pendingCount > 0  && `Hors-ligne — ${pendingCount} faute(s) en attente de synchronisation.`}
          {online  && pendingCount > 0  && `${pendingCount} faute(s) en attente de synchronisation.`}
        </Alert>
      )}

      {/* ── Add Fault form ── */}
      {canAddFault && (
        <>
          <Typography variant="subtitle2" gutterBottom sx={{ color: TEXT_PRI, fontWeight: 600, mb: 1.5 }}>
            Ajouter une faute
          </Typography>

          {error && (
            <Alert severity="error" sx={{
              mb: 1.5, borderRadius: '10px', bgcolor: '#fef2f2', color: '#991b1b',
              border: '1px solid #fecaca', '& .MuiAlert-icon': { color: '#dc2626' },
            }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          <Grid container spacing={1.5} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth size="small" label="Équipe"
                value={form.entityId} onChange={handleEntityChange}
                sx={inputSx} SelectProps={{ MenuProps: menuProps }}>
                {entities.map((e) => <MenuItem key={e._id} value={e._id}>{e.name}</MenuItem>)}
              </TextField>
            </Grid>

            <Grid item xs={6} sm={3}>
              <TextField fullWidth size="small" label="Minute" type="number"
                value={form.minute} onChange={handleChange('minute')}
                inputProps={{ min: 1, max: 120 }} sx={inputSx} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField select fullWidth size="small" label="Joueur"
                value={form.userId} onChange={handleChange('userId')}
                disabled={!form.entityId}
                sx={{ ...inputSx, '& .MuiOutlinedInput-root.Mui-disabled': { bgcolor: BG, '& fieldset': { borderColor: BORDER } } }}
                SelectProps={{ MenuProps: menuProps }}>
                {playersForEntity.map((p) => (
                  <MenuItem key={p._id} value={p._id}>{playerLabel(p)}</MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField select fullWidth size="small" label="Type de carton / faute"
                value={form.CardType} onChange={handleChange('CardType')}
                sx={inputSx} SelectProps={{ MenuProps: menuProps }}>
                {CARD_TYPES.map((c) => (
                  <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <Button
                variant="contained" size="small" onClick={handleSubmit} disabled={loading}
                startIcon={
                  loading ? <CircularProgress size={14} sx={{ color: '#fff' }} />
                          : online ? <Add /> : <WifiOff fontSize="small" />
                }
                sx={{
                  bgcolor: online ? ACCENT : '#b45309',
                  color: '#fff', borderRadius: '10px', textTransform: 'none',
                  fontWeight: 600, px: 2.5,
                  boxShadow: `0 4px 14px ${online ? ACCENT : '#b45309'}55`,
                  '&:hover': { bgcolor: online ? '#235f47' : '#92400e' },
                  '&.Mui-disabled': { bgcolor: BORDER, color: TEXT_SEC, boxShadow: 'none' },
                }}
              >
                {online ? 'Ajouter une faute' : 'Sauvegarder hors-ligne'}
              </Button>
            </Grid>
          </Grid>

          <Divider sx={{ mb: 1.5, borderColor: BORDER }} />
        </>
      )}

      {/* ── Faults list ── */}
      <Typography variant="subtitle2" gutterBottom sx={{ color: TEXT_PRI, fontWeight: 600, mb: 1.5 }}>
        Fautes ({faults.length})
      </Typography>

      {faults.length > 0 ? (
        <TableContainer component={Paper} elevation={0}
          sx={{ border: `1px solid ${BORDER}`, borderRadius: '12px', overflow: 'hidden' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: BG }}>
                {['Équipe', 'Joueur', 'Type', 'Minute'].map((label) => (
                  <TableCell key={label}>
                    <Typography variant="caption" sx={{
                      color: TEXT_SEC, fontWeight: 700,
                      textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: 11,
                    }}>
                      {label}
                    </Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {faults.map((f, i) => {
                const teamName = f.entityId?.name || (() => {
                  const uid = f.userId?._id || f.userId;
                  for (const entry of game?.players || []) {
                    const ids = (entry.userIds || []).map((u) => u._id);
                    if (ids.includes(uid)) return entry.entityId?.name || '—';
                  }
                  return '—';
                })();

                const isConfirming = confirmId === f._id;
                const isDeleting   = deletingId === f._id;

                return (
                  <React.Fragment key={f._id || i}>
                    <TableRow hover sx={{
                      bgcolor: isConfirming ? '#fffbeb' : SURFACE,
                      transition: 'background 0.15s',
                      '&:hover': { bgcolor: isConfirming ? '#fffbeb' : ACCENT_BG },
                      '& td': { borderColor: BORDER },
                    }}>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: TEXT_PRI, fontSize: 13 }}>{teamName}</Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Warning fontSize="small" sx={{ color: '#d97706', fontSize: 15 }} />
                          <Typography variant="body2" sx={{ color: TEXT_PRI, fontSize: 13 }}>
                            {f.userId?.firstName} {f.userId?.lastName}
                            {f.userId?.actualJerseyNumber ? ` #${f.userId.actualJerseyNumber}` : ''}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{
                            display: 'inline-flex', alignItems: 'center',
                            px: 1, py: 0.25, borderRadius: '6px',
                            border: `1px solid ${BORDER}`, bgcolor: BG,
                            color: TEXT_SEC, fontSize: 12, fontWeight: 500, whiteSpace: 'nowrap',
                          }}>
                            {getCardLabel(f.CardType)}
                          </Box>
                          {canAddFault && (
                            isDeleting ? (
                              <CircularProgress size={14} sx={{ color: '#dc2626' }} />
                            ) : isConfirming ? (
                              <Box sx={{ display: 'flex', gap: 0.5 }}>
                                <Tooltip title="Confirmer la suppression">
                                  <IconButton size="small" onClick={handleDeleteConfirm}
                                    sx={{ color: '#dc2626', p: 0.5 }}>
                                    <CheckCircle sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Annuler">
                                  <IconButton size="small" onClick={handleDeleteCancel}
                                    sx={{ color: TEXT_SEC, p: 0.5 }}>
                                    <Cancel sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            ) : (
                              <Tooltip title="Supprimer">
                                <Box component="button" onClick={() => handleDeleteRequest(f._id)}
                                  sx={{
                                    display: 'inline-flex', alignItems: 'center', gap: 0.4,
                                    px: 0.8, py: 0.25, borderRadius: '6px',
                                    border: '1px solid #fecaca', bgcolor: '#fef2f2',
                                    color: '#dc2626', fontSize: 11, fontWeight: 600, cursor: 'pointer',
                                    transition: 'all 0.15s',
                                    '&:hover': { bgcolor: '#fee2e2', borderColor: '#fca5a5' },
                                  }}>
                                  <Delete sx={{ fontSize: 12 }} />
                                </Box>
                              </Tooltip>
                            )
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: ACCENT, fontWeight: 700, fontSize: 13 }}>
                          {f.minute}'
                        </Typography>
                      </TableCell>
                    </TableRow>

                    {canAddFault && (
                      <TableRow sx={{ p: 0 }}>
                        <TableCell colSpan={4} sx={{ p: 0, border: 0 }}>
                          <Collapse in={isConfirming} unmountOnExit>
                            <Alert severity="warning" icon={<Warning fontSize="small" />}
                              sx={{
                                borderRadius: 0, py: 0.75, px: 2, bgcolor: '#fff8e1',
                                border: 'none', borderTop: '1px solid #fde68a', color: '#92400e',
                                '& .MuiAlert-icon': { color: '#d97706' },
                                '& .MuiAlert-message': { display: 'flex', alignItems: 'center', gap: 1, width: '100%' },
                              }}
                              action={
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                  <Button size="small" variant="contained" onClick={handleDeleteConfirm}
                                    startIcon={<Delete sx={{ fontSize: 14 }} />}
                                    sx={{
                                      bgcolor: '#dc2626', color: '#fff', borderRadius: '8px',
                                      textTransform: 'none', fontWeight: 600, fontSize: 12,
                                      boxShadow: 'none', '&:hover': { bgcolor: '#b91c1c', boxShadow: 'none' },
                                    }}>
                                    Supprimer
                                  </Button>
                                  <Button size="small" variant="outlined" onClick={handleDeleteCancel}
                                    sx={{
                                      borderRadius: '8px', textTransform: 'none', fontSize: 12,
                                      color: TEXT_SEC, borderColor: BORDER,
                                      '&:hover': { borderColor: TEXT_SEC, bgcolor: 'transparent' },
                                    }}>
                                    Annuler
                                  </Button>
                                </Box>
                              }
                            >
                              <Typography variant="body2" sx={{ fontSize: 13, color: '#92400e' }}>
                                Confirmer la suppression de cette faute ?
                              </Typography>
                            </Alert>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Typography variant="body2" sx={{ color: TEXT_SEC }}>Aucune faute pour l'instant</Typography>
      )}

      {/* ── Offline Faults Dialog ── */}
      <OfflineFaultsDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        pendingRows={pendingRows}
        allPlayers={allPlayers}
        entities={entities}
        onDelete={handleDeletePending}
        onDeleteAll={handleDeleteAllPending}
        syncing={syncing}
        onSync={syncPendingFaults}
        online={online}
      />

      {/* ── Snackbar ── */}
      <Snackbar
        open={snack.open} autoHideDuration={3000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack((s) => ({ ...s, open: false }))}
          sx={{
            borderRadius: '10px',
            ...(snack.severity === 'success' && {
              bgcolor: ACCENT_BG, color: ACCENT,
              border: `1px solid ${ACCENT}55`, fontWeight: 600,
              '& .MuiAlert-icon': { color: ACCENT },
              '& .MuiIconButton-root': { color: ACCENT },
            }),
          }}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FaultsTab;